/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Nishant Singh
*/

import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import { parseDate, currency } from '@global/helpers';
import Button from '@components/form/Button';
import Image from '@components/global/Image';
import StatusBadge from '@components/global/StatusBadge';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetCustomerClusterListQuery,
  useGetCustomerClusterCountQuery,
  useGetCustomerClusterInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  name: {
    head: 'Cluster Name',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium', textTransform: 'capitalize' },
  },
  purchased_range: { head: 'Purchased Range', body: 'purchased_range' },
  is_active: { head: 'Status', body: 'is_active', type: 'status_active' },
  last_updated: { head: 'Last Updated, by', body: 'last_updated' },
};

// Mobile-only search: the search box writes `name`; unwrap it into a $regex query.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  return filter;
};

// Display row (RTK cache frozen → new object): purchased range + last-updated.
const mapRow = (item) => ({
  ...item,
  id: item._id,
  purchased_range:
    item.from_purchased_range || item.to_purchased_range
      ? `${currency(item.from_purchased_range, false)} - ${currency(
          item.to_purchased_range,
          false,
        )}`
      : '',
  last_updated:
    item.updated_at || item.updated_by
      ? `${item.updated_at ? parseDate(item.updated_at) : '-'} | ${
          item.updated_by ? item.updated_by.name : '-'
        }`
      : '-',
});

const CustomerClusterScreen = (props) => {
  // ── selectors ──
  const userRole = useSelector((state) => state.auth.user.role);

  // ── derived ──
  const hasAddAccess = [
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ].includes(userRole && userRole.code);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetCustomerClusterListQuery,
    countHook: useGetCustomerClusterCountQuery,
    infiniteHook: useGetCustomerClusterInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── handlers ──
  const gotToDetail = (data) => {
    openChild('CustomerClusterDetailScreen', {
      titleScreen: data.name,
      name: data.name,
      id: data._id,
    });
  };

  const goToForm = () => {
    openChild('CustomerClusterFormScreen');
  };

  // ── effects ──
  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return) rather than the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : hasAddAccess ? (
          <MoreButton
            menu={[{ type: 'function', name: '+ Add', callback: goToForm }]}
            {...props}
          />
        ) : (
          false
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAddAccess]);

  // ── render ──
  const CreateClusterButton = () => (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {hasAddAccess ? (
        <Button
          type="primary"
          rounded={true}
          buttonStyle={{
            minWidth: 120,
            marginRight: 15,
            paddingHorizontal: 15,
          }}
          title={
            <SociollaText
              type="medium"
              style={{ fontSize: 14, color: Colors.white }}>
              + Create Cluster
            </SociollaText>
          }
          onPress={() => goToForm()}
        />
      ) : (
        false
      )}
    </View>
  );

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Customer Cluster"></Layout.Title>

          <Table
            filterName={scope}
            name="customer-cluster"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasCallback={true}
            showSearch={false}
            showFilter={false}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            onClickRow={({ data }) => gotToDetail(data)}
            moreInlineFilter={<CreateClusterButton></CreateClusterButton>}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          placeholder="Search by name"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.promotionItem}
              onPress={() => gotToDetail(data)}
              key={index}>
              <View style={[styles.promotionItemSection, { marginBottom: 10 }]}>
                <SociollaText type="medium" style={{ fontSize: 15 }}>
                  {data.name ? data.name : '-'}
                </SociollaText>
                <StatusBadge status={data.is_active ? 'active' : 'in_active'} />
              </View>
              <View
                style={[
                  styles.promotionItemSection,
                  { justifyContent: 'flex-start' },
                ]}>
                <Image
                  style={{
                    width: 18,
                    height: 18,
                    marginLeft: -5,
                    marginRight: 5,
                  }}
                  width={18}
                  height={18}
                  svg={true}
                  source={require('@assets/img/icons/menu/product-catalog.svg')}
                />
                <SociollaText style={{ fontSize: 12, color: Colors.grey1 }}>
                  {data.purchased_range}
                </SociollaText>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  promotionItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  promotionItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

export default CustomerClusterScreen;
